import { Link } from "react-router-dom";
import type { Article } from "@/types/melon";
import { getTopicTone, topicChipTone, topicIconTone } from "@/utils/tone";

type ArticleCardProps = {
  article: Article;
  variant?: "feature" | "standard" | "row";
  index?: number;
};

export default function ArticleCard({ article, variant = "standard", index }: ArticleCardProps) {
  const tone = getTopicTone(article.topicSlug);
  const chipTone = topicChipTone[tone];
  const iconTone = topicIconTone[tone];

  if (variant === "row") {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-5 border-b border-background-200 py-5 transition-colors hover:border-primary-300"
      >
        {typeof index === "number" ? (
          <span className="mt-1 w-6 shrink-0 font-heading text-xl font-bold text-background-400 transition-colors group-hover:text-primary-500">
            {String(index + 1).padStart(2, "0")}
          </span>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
            {article.title}
          </h3>
          <p className="line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className={`rounded-full border px-2 py-0.5 ${chipTone}`}>{article.topicName}</span>
            <span>{article.date}</span>
            <span className="text-background-400">·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
        <span className="mt-2 hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors group-hover:border-primary-400 group-hover:text-primary-600 sm:flex">
          <i className="ri-arrow-right-up-line"></i>
        </span>
      </Link>
    );
  }

  const isFeature = variant === "feature";

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/article/${article.slug}`} className="relative block w-full overflow-hidden" style={{ aspectRatio: isFeature ? "16 / 10" : "16 / 9" }}>
        <img
          src={article.cover}
          alt={article.coverAlt}
          title={`${article.title} - 热门大瓜深度解析`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className={`absolute left-3 top-3 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${iconTone}`}>
          <i className="ri-flashlight-fill text-xs"></i>
          热度 {article.heat}
        </span>
      </Link>
      <div className={`flex flex-1 flex-col ${isFeature ? "p-5 md:p-6" : "p-5"}`}>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={`rounded-full border px-2 py-0.5 ${chipTone}`}>{article.topicName}</span>
          <span className="text-foreground-500">{article.date}</span>
          <span className="text-foreground-500">· {article.readTime}</span>
        </div>
        <h3
          className={`mt-3 font-heading font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 ${
            isFeature ? "text-xl md:text-2xl" : "text-lg"
          }`}
        >
          <Link to={`/article/${article.slug}`}>{article.title}</Link>
        </h3>
        <p className={`mt-3 flex-1 leading-relaxed text-foreground-600 ${isFeature ? "text-sm md:text-[15px]" : "text-sm"}`}>
          {article.excerpt}
        </p>
        <div className="mt-5 flex items-center justify-between border-t border-background-100 pt-4 text-xs text-foreground-500">
          <span>{article.author} · {article.authorRole}</span>
          <span className="flex items-center gap-1 text-primary-600">
            阅读全文
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </article>
  );
}